import { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { LANGUAGES, t } from "@/i18n/translations";
import {
  Wifi, Snowflake, Bath, Car, Sun, UtensilsCrossed, BedDouble, KeyRound,
  MapPin, Phone, ChevronDown, Check,
} from "lucide-react";

const WHATSAPP_NUMBER = "306996125838"; // +30 699 612 5838

const IMG = {
  hero:     "https://images.pexels.com/photos/34939793/pexels-photo-34939793.jpeg",
  bedroom:  "https://images.pexels.com/photos/27626174/pexels-photo-27626174.jpeg",
  bathroom: "https://images.pexels.com/photos/6315801/pexels-photo-6315801.jpeg",
  living:   "https://images.unsplash.com/photo-1757439402190-99b73ac8e807",
  terrace:  "https://images.pexels.com/photos/17220587/pexels-photo-17220587.jpeg",
  // Additional curated images (Unsplash / Pexels)
  exterior: "https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=1600&q=80",
  pool:     "https://images.unsplash.com/photo-1602343168117-bb8ffe3e2e9f?auto=format&fit=crop&w=1600&q=80",
  table:    "https://images.unsplash.com/photo-1559599238-308793637427?auto=format&fit=crop&w=1600&q=80",
  bedroom2: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=1600&q=80",
  view:     "https://images.unsplash.com/photo-1533105079780-92b9be482077?auto=format&fit=crop&w=1600&q=80",
  detail:   "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1600&q=80",
};

// Reveal-on-scroll hook
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function LanguageSwitcher({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const current = LANGUAGES.find((l) => l.code === lang);
  return (
    <div ref={ref} className="relative">
      <button
        data-testid="language-switcher"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-ink hover:text-olive transition-colors"
      >
        {current?.short} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-3 w-44 bg-white border border-line shadow-[0_20px_50px_-20px_rgba(0,0,0,0.18)] py-2 z-50">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between hover:bg-sand transition-colors ${l.code === lang ? "text-olive" : "text-ink"}`}
            >
              <span className="font-body">{l.label}</span>
              {l.code === lang && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Nav({ lang, setLang, tr }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-line py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-20 flex items-center justify-between">
        <a href="#top" data-testid="brand-logo" className={`flex items-baseline gap-2 ${scrolled ? "text-ink" : "text-white"}`}>
          <span className="font-display text-2xl md:text-[26px] tracking-tight font-light leading-none">Casa di Cataleya</span>
          <span className={`hidden md:inline overline ${scrolled ? "text-olive" : "text-white/70"}`}>Zakynthos</span>
        </a>
        <nav className={`hidden lg:flex items-center gap-10 text-[12px] tracking-[0.18em] uppercase ${scrolled ? "text-ink" : "text-white"}`}>
          <a href="#villa"     data-testid="nav-villa"     className="link-underline">{tr.nav.stay}</a>
          <a href="#spaces"    data-testid="nav-spaces"    className="link-underline">{tr.nav.spaces}</a>
          <a href="#gallery"   data-testid="nav-gallery"   className="link-underline">{tr.nav.gallery}</a>
          <a href="#amenities" data-testid="nav-amenities" className="link-underline">{tr.nav.amenities}</a>
          <a href="#location"  data-testid="nav-location"  className="link-underline">{tr.nav.location}</a>
        </nav>
        <div className="flex items-center gap-5">
          <div className={scrolled ? "text-ink" : "text-white"}>
            <LanguageSwitcher lang={lang} setLang={setLang} />
          </div>
          <a
            data-testid="nav-reserve-btn"
            href="#booking"
            className={`hidden md:inline-block btn-press text-[11px] tracking-[0.22em] uppercase px-6 py-3 ${
              scrolled
                ? "bg-ink text-white hover:bg-olive"
                : "bg-white/95 text-ink hover:bg-olive hover:text-white"
            } transition-colors`}
          >
            {tr.nav.book}
          </a>
        </div>
      </div>
    </header>
  );
}

function Hero({ tr }) {
  return (
    <section id="top" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden grain">
      <div className="absolute inset-0">
        <img
          src={IMG.hero}
          alt="Casa di Cataleya — Mediterranean villa with sea view"
          className="w-full h-full object-cover kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/55" />
      </div>
      <div className="relative z-10 h-full flex flex-col">
        <div className="flex-1" />
        <div className="px-6 md:px-12 lg:px-20 pb-20 md:pb-28">
          <p className="overline text-white/85 reveal in">{tr.hero.overline}</p>
          <h1 className="mt-6 font-display font-light text-white leading-[0.95] tracking-tight reveal in">
            <span className="block text-[14vw] md:text-[8.5vw] lg:text-[7.2vw] italic">{tr.hero.title1}</span>
            <span className="block text-[16vw] md:text-[10vw] lg:text-[8.5vw] -mt-2">{tr.hero.title2}</span>
          </h1>
          <p className="mt-8 max-w-xl text-white/85 text-base md:text-lg font-light leading-relaxed reveal in">
            {tr.hero.subtitle}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4 reveal in">
            <a
              data-testid="hero-reserve-btn"
              href="#booking"
              className="btn-press inline-flex items-center gap-3 bg-white text-ink px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-olive hover:text-white transition-colors"
            >
              {tr.hero.cta}
            </a>
            <a
              data-testid="hero-discover-btn"
              href="#villa"
              className="btn-press inline-flex items-center gap-3 border border-white/70 text-white px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-white hover:text-ink transition-colors"
            >
              {tr.hero.secondary}
            </a>
          </div>
        </div>
      </div>
      {/* Side marker */}
      <div className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-10 flex-col items-center gap-3 text-white/80">
        <span className="w-px h-16 bg-white/40" />
        <span className="rotate-180 [writing-mode:vertical-rl] overline">37.78°N · 20.89°E</span>
        <span className="w-px h-16 bg-white/40" />
      </div>
    </section>
  );
}

function Intro({ tr }) {
  return (
    <section id="villa" className="py-24 md:py-36 px-6 md:px-12 lg:px-20 bg-background">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-start">
        <div className="lg:col-span-4 reveal">
          <p className="overline">{tr.intro.overline}</p>
        </div>
        <div className="lg:col-span-8 reveal">
          <h2 className="font-display font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {tr.intro.title}
          </h2>
          <p className="mt-8 max-w-2xl text-muted text-lg leading-relaxed font-light">
            {tr.intro.body}
          </p>
        </div>
      </div>
    </section>
  );
}

function Floor({ image, tag, title, desc, features, reverse }) {
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <div className="lg:col-span-7 img-hover reveal">
        <img src={image} alt={title} className="w-full h-[420px] md:h-[560px] object-cover" />
      </div>
      <div className="lg:col-span-5 reveal">
        <p className="overline">{tag}</p>
        <h3 className="mt-4 font-display font-light text-ink text-3xl md:text-4xl leading-tight">{title}</h3>
        <p className="mt-6 text-muted leading-relaxed">{desc}</p>
        <ul className="mt-8 space-y-3">
          {features.map((f) => (
            <li key={f} className="flex items-center gap-3 text-ink">
              <span className="w-6 h-px bg-olive" />
              <span className="text-sm tracking-wide">{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Spaces({ tr }) {
  return (
    <section id="spaces" className="py-24 md:py-36 px-6 md:px-12 lg:px-20 bg-white">
      <div className="max-w-3xl mb-20 reveal">
        <p className="overline">{tr.spaces.overline}</p>
        <h2 className="mt-4 font-display font-light text-ink text-4xl md:text-5xl leading-tight tracking-tight">
          {tr.spaces.title}
        </h2>
      </div>
      <div className="space-y-24">
        <Floor image={IMG.living}  tag={tr.spaces.floor1.tag} title={tr.spaces.floor1.title} desc={tr.spaces.floor1.desc} features={tr.spaces.features} />
        <Floor image={IMG.terrace} tag={tr.spaces.floor2.tag} title={tr.spaces.floor2.title} desc={tr.spaces.floor2.desc} features={tr.spaces.features} reverse />
      </div>
    </section>
  );
}

function Gallery({ tr }) {
  // Editorial bento grid
  return (
    <section id="gallery" className="py-24 md:py-36 px-6 md:px-12 lg:px-20 bg-background">
      <div className="max-w-3xl mb-16 reveal">
        <p className="overline">{tr.gallery.overline}</p>
        <h2 className="mt-4 font-display font-light text-ink text-4xl md:text-5xl tracking-tight">
          {tr.gallery.title}
        </h2>
      </div>

      <div className="grid grid-cols-12 gap-3 md:gap-5">
        <div className="col-span-12 md:col-span-7 img-hover reveal" data-testid="gallery-img-1">
          <img src={IMG.bedroom} alt="Bedroom" className="w-full h-[320px] md:h-[520px] object-cover" />
        </div>
        <div className="col-span-12 md:col-span-5 img-hover reveal" data-testid="gallery-img-2">
          <img src={IMG.bathroom} alt="Bathroom" className="w-full h-[320px] md:h-[520px] object-cover" />
        </div>
        <div className="col-span-6 md:col-span-4 img-hover reveal" data-testid="gallery-img-3">
          <img src={IMG.exterior} alt="Exterior" className="w-full h-[220px] md:h-[340px] object-cover" />
        </div>
        <div className="col-span-6 md:col-span-4 img-hover reveal" data-testid="gallery-img-4">
          <img src={IMG.table} alt="Dining" className="w-full h-[220px] md:h-[340px] object-cover" />
        </div>
        <div className="col-span-12 md:col-span-4 img-hover reveal" data-testid="gallery-img-5">
          <img src={IMG.bedroom2} alt="Bedroom detail" className="w-full h-[220px] md:h-[340px] object-cover" />
        </div>
        <div className="col-span-12 md:col-span-8 img-hover reveal" data-testid="gallery-img-6">
          <img src={IMG.view} alt="Sea view" className="w-full h-[320px] md:h-[480px] object-cover" />
        </div>
        <div className="col-span-12 md:col-span-4 img-hover reveal" data-testid="gallery-img-7">
          <img src={IMG.detail} alt="Interior detail" className="w-full h-[320px] md:h-[480px] object-cover" />
        </div>
      </div>
    </section>
  );
}

const AMENITY_ICONS = [Wifi, Snowflake, Bath, Car, Sun, UtensilsCrossed, BedDouble, KeyRound];

function Amenities({ tr }) {
  return (
    <section id="amenities" className="py-24 md:py-36 px-6 md:px-12 lg:px-20 bg-ink text-white">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-4 reveal">
          <p className="overline text-olive">{tr.amenities.overline}</p>
          <h2 className="mt-4 font-display font-light text-4xl md:text-5xl leading-tight tracking-tight">
            {tr.amenities.title}
          </h2>
        </div>
        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-10">
          {tr.amenities.list.map((a, i) => {
            const Icon = AMENITY_ICONS[i] || Wifi;
            return (
              <div key={a.t} className="reveal" data-testid={`amenity-${i}`}>
                <div className="flex items-center gap-4 mb-3">
                  <span className="w-10 h-10 border border-white/30 flex items-center justify-center">
                    <Icon size={18} className="text-white" />
                  </span>
                  <h3 className="font-display text-2xl font-light">{a.t}</h3>
                </div>
                <p className="text-white/65 text-sm leading-relaxed pl-14">{a.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Location({ tr }) {
  return (
    <section id="location" className="py-24 md:py-36 px-6 md:px-12 lg:px-20 bg-background">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 reveal">
          <p className="overline">{tr.location.overline}</p>
          <h2 className="mt-4 font-display font-light text-4xl md:text-5xl leading-tight tracking-tight text-ink">
            {tr.location.title}
          </h2>
          <p className="mt-6 text-muted leading-relaxed text-lg max-w-xl">{tr.location.body}</p>
          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
            {tr.location.points.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <MapPin size={16} className="text-olive mt-1 flex-shrink-0" />
                <span className="text-ink text-sm">{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6 reveal">
          <div className="relative">
            <iframe
              data-testid="location-map"
              title="Zakynthos map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=20.62%2C37.69%2C21.10%2C37.94&layer=mapnik&marker=37.787%2C20.899"
              className="w-full h-[420px] md:h-[520px] border-0 grayscale-[20%]"
              loading="lazy"
            />
            <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-4 py-3 border border-line">
              <p className="overline">Zakynthos</p>
              <p className="font-display text-lg text-ink">Casa di Cataleya</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BookingForm({ tr, lang }) {
  const [form, setForm] = useState({
    first: "", last: "", country: "", arrival: "", departure: "", guests: "2", floor: "any", message: "",
  });
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildMessage = () => {
    const fl = {
      any: tr.booking.floorAny,
      ground: tr.booking.floorGround,
      upper: tr.booking.floorUpper,
      both: tr.booking.floorBoth,
    }[form.floor];

    const lines = [
      `*${tr.waMsg.header}*`,
      ``,
      `${tr.waMsg.name}: ${form.first} ${form.last}`.trim(),
      `${tr.waMsg.country}: ${form.country}`,
      `${tr.waMsg.dates}: ${form.arrival} → ${form.departure}`,
      `${tr.waMsg.guests}: ${form.guests}`,
      `${tr.waMsg.floor}: ${fl}`,
    ];
    if (form.message.trim()) {
      lines.push(``, `${tr.waMsg.notes}: ${form.message.trim()}`);
    }
    return lines.join("\n");
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.first || !form.last || !form.arrival || !form.departure || !form.country) {
      setError(tr.booking.required);
      return;
    }
    setError("");
    const msg = encodeURIComponent(buildMessage());
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;
    window.open(url, "_blank");
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-line py-3 px-0 text-ink placeholder:text-muted/70 focus:outline-none focus:border-olive transition-colors";
  const labelCls = "overline mb-2 block";

  return (
    <section id="booking" className="relative py-24 md:py-36 px-6 md:px-12 lg:px-20 bg-sand">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5 reveal">
          <p className="overline">{tr.booking.overline}</p>
          <h2 className="mt-4 font-display font-light text-4xl md:text-5xl leading-tight tracking-tight text-ink">
            {tr.booking.title}
          </h2>
          <p className="mt-6 text-muted leading-relaxed max-w-md">{tr.booking.body}</p>

          <div className="mt-10 border-t border-line pt-8">
            <p className="overline mb-3">{tr.booking.waLabel}</p>
            <a
              data-testid="direct-wa-link"
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank" rel="noreferrer"
              className="font-display text-2xl text-ink link-underline inline-flex items-center gap-2"
            >
              <Phone size={18} className="text-olive" /> +30 699 612 5838
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 reveal" data-testid="booking-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
            <div>
              <label className={labelCls}>{tr.booking.first}</label>
              <input data-testid="input-first" type="text" value={form.first} onChange={onChange("first")} className={inputCls} required />
            </div>
            <div>
              <label className={labelCls}>{tr.booking.last}</label>
              <input data-testid="input-last" type="text" value={form.last} onChange={onChange("last")} className={inputCls} required />
            </div>
            <div>
              <label className={labelCls}>{tr.booking.country}</label>
              <input data-testid="input-country" type="text" value={form.country} onChange={onChange("country")} className={inputCls} required />
            </div>
            <div>
              <label className={labelCls}>{tr.booking.guests}</label>
              <input data-testid="input-guests" type="number" min="1" max="12" value={form.guests} onChange={onChange("guests")} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{tr.booking.arrival}</label>
              <input data-testid="input-arrival" type="date" value={form.arrival} onChange={onChange("arrival")} className={inputCls} required />
            </div>
            <div>
              <label className={labelCls}>{tr.booking.departure}</label>
              <input data-testid="input-departure" type="date" value={form.departure} onChange={onChange("departure")} className={inputCls} required />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>{tr.booking.floor}</label>
              <select data-testid="input-floor" value={form.floor} onChange={onChange("floor")} className={`${inputCls} appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%228%22 viewBox=%220 0 12 8%22><path d=%22M1 1l5 5 5-5%22 stroke=%22%23748C69%22 stroke-width=%221.5%22 fill=%22none%22/></svg>')] bg-no-repeat bg-[right_0.5rem_center] pr-8`}>
                <option value="any">{tr.booking.floorAny}</option>
                <option value="ground">{tr.booking.floorGround}</option>
                <option value="upper">{tr.booking.floorUpper}</option>
                <option value="both">{tr.booking.floorBoth}</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>{tr.booking.message}</label>
              <textarea
                data-testid="input-message"
                rows={3}
                value={form.message}
                onChange={onChange("message")}
                placeholder={tr.booking.messagePh}
                className={`${inputCls} resize-none`}
              />
            </div>
          </div>

          {error && (
            <p data-testid="form-error" className="mt-6 text-sm text-terracotta">{error}</p>
          )}

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
            <button
              type="submit"
              data-testid="submit-whatsapp-btn"
              className="btn-press inline-flex items-center justify-center gap-3 bg-whatsapp hover:bg-whatsapp-hover text-white px-10 py-4 text-[11px] tracking-[0.25em] uppercase transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M.057 24l1.687-6.163A11.867 11.867 0 010 11.86C0 5.322 5.335 0 11.892 0a11.83 11.83 0 018.413 3.488 11.78 11.78 0 013.48 8.392c0 6.538-5.335 11.86-11.893 11.86a11.9 11.9 0 01-5.688-1.45L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.231 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.139-.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.298-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
              </svg>
              {tr.booking.submit}
            </button>
            <p className="text-xs text-muted max-w-sm">{tr.booking.note}</p>
          </div>
        </form>
      </div>
    </section>
  );
}

function Footer({ tr }) {
  return (
    <footer className="bg-ink text-white py-16 px-6 md:px-12 lg:px-20">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <p className="font-display text-3xl font-light">Casa di Cataleya</p>
          <p className="mt-3 text-white/60 text-sm leading-relaxed max-w-xs">{tr.footer.tagline}</p>
        </div>
        <div>
          <p className="overline text-olive">{tr.footer.contact}</p>
          <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="mt-3 block text-white link-underline w-fit">
            +30 699 612 5838
          </a>
          <p className="mt-2 text-white/60 text-sm">Zakynthos, Greece</p>
          <p className="mt-1 text-white/60 text-sm">casadicataleyaa.com</p>
        </div>
        <div className="md:text-right">
          <p className="overline text-olive">Casa di Cataleya</p>
          <p className="mt-3 text-white/60 text-sm">© {new Date().getFullYear()} · {tr.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
}

// Sticky floating WhatsApp
function FloatingWA() {
  return (
    <a
      data-testid="floating-wa-btn"
      href={`https://wa.me/${WHATSAPP_NUMBER}`}
      target="_blank" rel="noreferrer"
      className="fixed bottom-5 left-5 z-40 bg-whatsapp hover:bg-whatsapp-hover text-white w-14 h-14 rounded-full flex items-center justify-center shadow-lg shadow-black/20 transition-colors"
      aria-label="Chat on WhatsApp"
    >
      <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M.057 24l1.687-6.163A11.867 11.867 0 010 11.86C0 5.322 5.335 0 11.892 0a11.83 11.83 0 018.413 3.488 11.78 11.78 0 013.48 8.392c0 6.538-5.335 11.86-11.893 11.86a11.9 11.9 0 01-5.688-1.45L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.231 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.139-.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.298-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
      </svg>
    </a>
  );
}

function App() {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    const stored = localStorage.getItem("cataleya_lang");
    if (stored && t[stored]) return stored;
    const nav = (navigator.language || "en").slice(0, 2);
    return t[nav] ? nav : "en";
  });
  useEffect(() => { localStorage.setItem("cataleya_lang", lang); }, [lang]);
  const tr = useMemo(() => t[lang] || t.en, [lang]);

  useReveal();

  return (
    <div className="App font-body bg-background text-ink">
      <Nav lang={lang} setLang={setLang} tr={tr} />
      <Hero tr={tr} />
      <Intro tr={tr} />
      <Spaces tr={tr} />
      <Gallery tr={tr} />
      <Amenities tr={tr} />
      <Location tr={tr} />
      <BookingForm tr={tr} lang={lang} />
      <Footer tr={tr} />
      <FloatingWA />
    </div>
  );
}

export default App;
